import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  StyleSheet,
  View,
  Animated,
  TouchableOpacity,
  TouchableWithoutFeedback,
  Dimensions,
  Platform,
} from "react-native";

import { DEFAULT_ACTIVE_OPACITY, buttonStyle } from "./utils";

import ActionButtonItem from "./ActionButtonItem";
import MariIcon from "./mari/Mari";
import { isWeb } from "../../../../helpers/utils";
import AppConfig from "../../../../config/AppConfig";
import ExitTutorialsButton from "../header/ExitTutorialsButton";

const { height: FULL_HEIGHT, width: FULL_WIDTH } = Dimensions.get("window");
const { bottom: BUTTON_BOTTOM, right: BUTTON_RIGHT } = buttonStyle;

const ActionButton = React.forwardRef((props, ref) => {
  const [mariOptions, setMariOptions] = useState(props.mariOptions || {});
  const [mariMessage, setMariMessage] = useState(mariOptions.mariMessage);
  const [showTutorials, setShowTutorials] = useState(mariOptions.showTutorials);
  const [showOverlay, setShowOverlay] = useState(mariOptions.showOverlay);
  const [showActions, setShowActions] = useState(false);
  const [animation] = useState(new Animated.Value(active ? 1 : 0));
  const [active, setActive] = useState(
    props.active || mariOptions.showTutorials,
  );

  useEffect(() => {
    const { showTutorials: tu, mariMessage: msg, showOverlay: so } =
      props.mariOptions || {};
    setActive(!!so);
    setShowOverlay(so);
    setShowTutorials(tu);
    setMariMessage(msg);
    setMariOptions(props.mariOptions || {});

    if (so) {
      animation.setValue(1);
    }
  }, [props.mariOptions]);

  const animateButton = (animate = true) => {
    if (showActions || (showTutorials && mariOptions.hideActions)) {
      return reset();
    }

    if (animate) {
      Animated.spring(animation, { toValue: 1 }).start();
    } else {
      animation.setValue(1);
    }

    setActive(true);

    if (mariOptions.hideActions) return;
    setShowActions(true);
  };

  const reset = (animate = true) => {
    if (props.onReset) props.onReset();

    if (!showOverlay) {
      setActive(false);

      if (animate) {
        Animated.spring(animation, { toValue: 0 }).start();
      } else {
        animation.setValue(0);
      }
    }

    setTimeout(() => {
      setShowActions(false);
    }, 250);
  };

  const _renderActions = () => {
    const { children } = props;

    if (!showActions) return null;

    let actionButtons = !Array.isArray(children) ? [children] : children;
    actionButtons = actionButtons.filter(
      (actionButton) => typeof actionButton == "object",
    );

    return (
      <View style={styles.actionStyle} pointerEvents={"box-none"}>
        {actionButtons.map((button, idx) => (
          <ActionButtonItem
            key={idx}
            anim={animation}
            {...props}
            {...button.props}
            parentSize={props.size}
            size={44}
            btnColor={props.btnOutRange}
            onPress={() => handleActionClick(button)}
          />
        ))}
      </View>
    );
  };

  const handleActionClick = (button) => {
    Animated.spring(animation, { toValue: 0 }).start();
    setActive(false);
    setShowActions(false);
    if (button.props.onPress) button.props.onPress();
  };

  const _handleMariClick = (clearMessage = true) => {
    if (showTutorials) {
      //setMariMessage("");
      props.onPress();
      if (props.children) animateButton();
    } else if (clearMessage && mariMessage) {
      //setMariOptions({ ...props.mariOptions, mariMessage: "" });
      setMariMessage("");
    } else {
      props.onPress();
      if (props.children) animateButton();
    }
  };

  return (
    <TouchableWithoutFeedback
      style={[
        styles.container,
        active && {
          height: FULL_HEIGHT,
        },
      ]}
      onPress={(e) => {
        e.preventDefault();
        reset();
      }}
    >
      <View
        style={[
          styles.container,
          active && {
            height: FULL_HEIGHT,
          },
        ]}
      >
        {/* Exit tutorials */}
        {showOverlay ? (
          <ExitTutorialsButton style={{ top: isWeb ? 55 : 140, right: 5 }} />
        ) : null}

        {/* Bg overlay */}
        {active ? (
          <Animated.View
            style={[
              styles.bgOverlay,
              { bottom: props.showFooter ? 80 : 0 },
              {
                opacity: animation.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, props.bgOpacity],
                }),
              },
            ]}
          >
            {props.backdrop}
          </Animated.View>
        ) : null}

        {/* Buttons */}
        <View style={styles.mediaContainer}>
          {props.children ? _renderActions() : null}

          {/* Mari Icon */}
          <TouchableOpacity
            style={styles.mariIconContainer}
            testID={props.testID}
            accessible={props.accessible}
            accessibilityLabel={props.accessibilityLabel}
            activeOpacity={props.activeOpacity}
            onLongPress={props.onLongPress}
            onPressIn={props.onPressIn}
            onPressOut={props.onPressOut}
          >
            <MariIcon
              mariOptions={{
                ...mariOptions,
                mariMessage: mariMessage,
                isSelected: showActions ? false : mariOptions.isSelected,
              }}
              isMenuActive={active}
              onPress={_handleMariClick}
              source={props.source}
            />
          </TouchableOpacity>
        </View>
      </View>
    </TouchableWithoutFeedback>
  );
});

ActionButton.Item = ActionButtonItem;

ActionButton.propTypes = {
  resetToken: PropTypes.any,
  active: PropTypes.bool,

  position: PropTypes.string,
  zIndex: PropTypes.number,

  hideShadow: PropTypes.bool,
  shadowStyle: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.number,
  ]),

  renderIcon: PropTypes.func,

  bgColor: PropTypes.string,
  bgOpacity: PropTypes.number,
  buttonColor: PropTypes.string,
  buttonTextStyle: PropTypes.any,
  buttonText: PropTypes.string,

  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
  spacing: PropTypes.number,
  size: PropTypes.number,
  autoInactive: PropTypes.bool,
  onPress: PropTypes.func,
  onPressIn: PropTypes.func,
  onPressOut: PropTypes.func,
  backdrop: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  degrees: PropTypes.number,
  verticalOrientation: PropTypes.oneOf(["up", "down"]),
  backgroundTappable: PropTypes.bool,
  activeOpacity: PropTypes.number,

  useNativeFeedback: PropTypes.bool,
  fixNativeFeedbackRadius: PropTypes.bool,
  nativeFeedbackRippleColor: PropTypes.string,

  testID: PropTypes.string,
  accessibilityLabel: PropTypes.string,
  accessible: PropTypes.bool,
};

ActionButton.defaultProps = {
  resetToken: null,
  active: false,
  bgColor: "transparent",
  bgOpacity: 0.84,
  buttonColor: "#000",
  buttonTextStyle: {},
  buttonText: "+",
  spacing: 10,
  outRangeScale: 1,
  autoInactive: true,
  onPress: () => {},
  onPressIn: () => {},
  onPressOn: () => {},
  backdrop: false,
  degrees: 45,
  position: "right",
  offsetX: 0,
  offsetY: 0,
  size: 58,
  verticalOrientation: "up",
  backgroundTappable: false,
  useNativeFeedback: true,
  activeOpacity: DEFAULT_ACTIVE_OPACITY,
  fixNativeFeedbackRadius: false,
  nativeFeedbackRippleColor: "rgba(255,255,255,0.75)",
  testID: undefined,
  accessibilityLabel: undefined,
  accessible: undefined,
};

export default ActionButton;

const styles = StyleSheet.create({
  container: {
    height: "100%",
    justifyContent: "flex-end",
    maxWidth: AppConfig.MAX_WIDTH,
  },
  mediaContainer: {
    height: "auto",
    backgroundColor: "transparent",
    display: "flex",
    alignItems: "flex-end",
  },
  bgOverlay: {
    backgroundColor: "rgba(255, 255, 255, 1)",
    position: "absolute",
    left: 0,
    bottom: 0,
    width: FULL_WIDTH,
    height: FULL_HEIGHT,
    opacity: 0.84,
    transform: [
      { translateY: isWeb ? BUTTON_BOTTOM : 0 },
      //{ translateX: BUTTON_RIGHT },
    ],
  },
  mariIconContainer: {
    right: 0,
    bottom: 0,
  },
  btnText: {
    marginBottom: 2,
    marginLeft: 2,
    fontSize: 24,
    backgroundColor: "transparent",
  },
  actionStyle: {
    width: FULL_WIDTH,
    //flex: 1,
    //alignSelf: "stretch",
    //justifyContent: "flex-end",
    minHeight: 50,
  },
  exitButton: {
    position: "absolute",
    right: 0,
    top: 80,
    backgroundColor: "#F4F6FA",
    borderRadius: 100,
    borderWidth: 1,
    borderColor: "#F4F6FA",
    zIndex: 100,
    paddingHorizontal: 10,
  },
  exitButtonText: {
    fontFamily:  Platform.select({ default: "Montserrat", android: "Montserrat-SemiBold" }),
    fontWeight: "600",
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 0.175,
    color: "#828282",
  },
});
